import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { toast } from "sonner";
import CaptchaTokenService from "./captchaToken.service";
import { useAltcha } from "./useAltcha.hook";

export type AltchaAutoTokenOptions = {
	action?: string;
	intervalMs?: number;
	enabled?: boolean;
	onToken?: (token: string) => void;
};

/**
 * Periodically solves Altcha challenges and stores the payload in CaptchaTokenService.
 * Returns the widget ref, a refreshNow() for manual refresh, and hasActiveToken state.
 *
 * Usage:
 * ```tsx
 * const { widgetRef, hasActiveToken } = useAltchaAutoToken({
 *   action: "login",
 *   intervalMs: 90_000,
 * });
 * ```
 *
 * Pair with `<AltchaWidget>` to render the widget in the DOM.
 */
export function useAltchaAutoToken(options?: AltchaAutoTokenOptions) {
	const intervalMs = options?.intervalMs ?? 90_000;
	const enabled = options?.enabled ?? true;

	const onTokenRef = useRef<AltchaAutoTokenOptions["onToken"]>(
		options?.onToken,
	);
	useEffect(() => {
		onTokenRef.current = options?.onToken;
	}, [options?.onToken]);

	const { widgetRef, solveCaptcha } = useAltcha({ enabled });
	const solveCaptchaRef = useRef(solveCaptcha);
	solveCaptchaRef.current = solveCaptcha;

	const hasActiveToken = useSyncExternalStore(
		(cb) => CaptchaTokenService.getInstance().subscribe(() => cb()),
		() => CaptchaTokenService.getInstance().isTokenAvailable(),
		() => false,
	);

	const timerRef = useRef<number | null>(null);

	const refreshNow = useCallback(async () => {
		const token = await solveCaptchaRef.current();
		CaptchaTokenService.getInstance().setToken(token);
		onTokenRef.current?.(token);
		return token;
	}, []);

	useEffect(() => {
		if (!enabled) return;

		// Clear any leftover bot-detection toasts from earlier hook versions.
		toast.dismiss("captcha-bot-detected");

		let cancelled = false;

		const schedule = (delay: number) => {
			if (timerRef.current) window.clearTimeout(timerRef.current);
			timerRef.current = window.setTimeout(() => {
				void run();
			}, delay);
		};

		const run = async () => {
			try {
				await refreshNow();
				if (!cancelled && enabled) schedule(intervalMs);
			} catch (err) {
				if (cancelled || !enabled) return;
				const delay =
					err instanceof Error && /not ready/i.test(err.message)
						? 1_000
						: 5_000;
				schedule(delay);
			}
		};

		void run();

		return () => {
			cancelled = true;
			if (timerRef.current) window.clearTimeout(timerRef.current);
		};
	}, [enabled, intervalMs, refreshNow]);

	return { widgetRef, refreshNow, hasActiveToken } as const;
}

export default useAltchaAutoToken;
